(function (angular, app, window) {
    'use strict';

    var LOCAL_STORAGE_KEYS = {
        SESSION: 'session',
        BRANCH_ID: 'branchId',
        AREA_ID: 'areaId',
        AREA_NAME: 'area'
    };
    var BLOB_KEY = 'frontend';
    var SESSION_DISK_KEY = 'frontendSession';
    var ALGORITHM = 'AES-GCM';
    var IV_LENGTH = 12;
    var DB_NAME = 'sp-storage-key';
    var STORE_NAME = 'keys';
    var KEY_ID = 'device-aes-key';
    var NATIVE_STORAGE_KEY = 'device-aes-key';
    var KEY_BYTE_LENGTH = 32;

    app.service('LocalStorage', ['$location', '$window', '$q', 'SP_SERVICES',
        function ($location, $window, $q, SP_SERVICES) {
            var self = this,
                hasLocalStorage = true,
                frontendLocalStorage = {},
                _searchParams = $location.search(),
                _isLoginAs = _searchParams && _searchParams.userId !== undefined && _searchParams.token !== undefined,
                _loginAsArea,
                sessionCache,
                sessionLoaded = false,
                cryptoKey = null,
                bootstrapPromise;

            angular.extend(self, {
                getItem: getItem,
                setItem: setItem,
                removeItem: removeItem,
                clear: clear,
                isExist: isExist,
                ready: ready
            });

            try {
                if (!_isLoginAs) {
                    frontendLocalStorage = JSON.parse(window.localStorage.getItem(BLOB_KEY) || '{}');
                }
            } catch (err) {
                hasLocalStorage = false;
            }

            _startBootstrap();

            function ready() {
                return bootstrapPromise || $q.resolve();
            }

            function _startBootstrap() {
                if (!_isSecureLocalStorageEnabled() || _isLoginAs) {
                    bootstrapPromise = $q.resolve();
                    return;
                }

                bootstrapPromise = _bootstrap();
            }

            function _isSecureLocalStorageEnabled() {
                var settings = window.sp &&
                    window.sp.frontendData &&
                    window.sp.frontendData.retailer &&
                    window.sp.frontendData.retailer.settings;

                return !!(settings && settings.isSecureLocalStorageEnabled === 'true');
            }

            function _isWebCryptoAvailable() {
                return !!($window.isSecureContext && $window.crypto && $window.crypto.subtle && $window.crypto.getRandomValues);
            }

            function _shouldEncryptSession() {
                return _isSecureLocalStorageEnabled() && _isWebCryptoAvailable();
            }

            function _bootstrap() {
                if (!_isSecureLocalStorageEnabled()) {
                    return $q.resolve();
                }

                if (!_shouldEncryptSession()) {
                    return _loadSessionFromDiskPlain();
                }

                return _getOrCreateKey().then(function (key) {
                    cryptoKey = key;
                    return _loadSessionFromDisk();
                }).catch(function (err) {
                    console.warn('[LocalStorage] Failed to bootstrap secure session storage', err);
                    return _loadSessionFromDiskPlain();
                });
            }

            function save() {
                if (!hasLocalStorage || _isLoginAs) {
                    return;
                }

                var blobToSave = angular.extend({}, frontendLocalStorage);

                if (_isSecureLocalStorageEnabled()) {
                    if (_hasMigratedSession()) {
                        delete blobToSave[LOCAL_STORAGE_KEYS.SESSION];
                    } else {
                        var legacySession = _readLegacyBlobSession();
                        if (legacySession) {
                            blobToSave[LOCAL_STORAGE_KEYS.SESSION] = legacySession;
                        } else {
                            delete blobToSave[LOCAL_STORAGE_KEYS.SESSION];
                        }
                    }
                }

                window.localStorage.setItem(BLOB_KEY, JSON.stringify(blobToSave));
            }

            function _hasMigratedSession() {
                return sessionLoaded || !!window.localStorage.getItem(SESSION_DISK_KEY);
            }

            function getItem(key) {
                if (_isLoginAs) {
                    return _handleLoginAs(key);
                }

                if (key === LOCAL_STORAGE_KEYS.SESSION && _isSecureLocalStorageEnabled()) {
                    if (sessionLoaded) {
                        return sessionCache;
                    }

                    var raw = window.localStorage.getItem(SESSION_DISK_KEY);
                    if (!raw) {
                        return _readLegacyBlobSession();
                    }

                    if (!_isEncryptedEnvelope(raw)) {
                        try {
                            return JSON.parse(raw);
                        } catch (err) {
                            return raw;
                        }
                    }

                    return sessionCache || _readLegacyBlobSession();
                }

                return frontendLocalStorage[key];
            }

            function _handleLoginAs(key) {
                if (key === LOCAL_STORAGE_KEYS.SESSION) {
                    var organization;
                    if (_searchParams.organization) {
                        try {
                            organization = JSON.parse(_searchParams.organization);
                        } catch(err) {}
                    }

                    return {
                        token: _searchParams.token,
                        userId: _searchParams.userId,
                        branchKeyOrganization: organization && organization.id,
                        organizationRoleId: organization && organization.roleId,
                        organizationBranches: organization && organization.branches && organization.branches.map(function(branch) {
                            return branch.id;
                        })
                    };
                } else if (key === LOCAL_STORAGE_KEYS.BRANCH_ID) {
                    return Number(_searchParams.branchId) || null;
                }

                if (key !== LOCAL_STORAGE_KEYS.AREA_ID && key !== LOCAL_STORAGE_KEYS.AREA_NAME) {
                    return frontendLocalStorage[key];
                }

                if (_searchParams.branchId === undefined) {
                    return frontendLocalStorage[key];
                }

                if (!_loginAsArea) {
                    var branch = $window.sp.frontendData.retailer.branches.find(function(branch) {
                        return branch.id === Number(_searchParams.branchId);
                    });

                    if (!branch || !branch.areas || !branch.areas.length) {
                        return;
                    }

                    _loginAsArea = branch.areas.find(function(area) {
                        return area.names && area.names.length && area.deliveryTypeId !== SP_SERVICES.DELIVERY_TYPES.PICKUP;
                    });
                    var parsedFeLocalStorage = {};
                    var feLocalStorage = window.localStorage.getItem(BLOB_KEY);
                    if (feLocalStorage) {
                        parsedFeLocalStorage = JSON.parse(feLocalStorage);
                    }

                    if (!_loginAsArea && !parsedFeLocalStorage.area && !parsedFeLocalStorage.areaId) {
                        _loginAsArea = branch.areas[0];
                    } else if (parsedFeLocalStorage) {
                        var storedArea = branch.areas.find(function(area) {
                            return area.name == parsedFeLocalStorage.area && area.id === parsedFeLocalStorage.areaId && area.deliveryTypeId !== SP_SERVICES.DELIVERY_TYPES.PICKUP;
                        });

                        if (storedArea) {
                            _loginAsArea = storedArea;
                        } else {
                            _loginAsArea = branch.areas[0];
                        }
                    }
                }

                if (!_loginAsArea.names || !_loginAsArea.names.length) {
                    return;
                }

                if (key === LOCAL_STORAGE_KEYS.AREA_ID) {
                    return _loginAsArea.id;
                }

                return _loginAsArea.names[0].name;
            }

            function setItem(key, val) {
                if (key === LOCAL_STORAGE_KEYS.SESSION && _isSecureLocalStorageEnabled()) {
                    sessionCache = _normalizeSessionValue(val);
                    sessionLoaded = true;
                    delete frontendLocalStorage[LOCAL_STORAGE_KEYS.SESSION];
                    save();

                    if (!_shouldEncryptSession()) {
                        _writePlainSession(sessionCache);
                        return;
                    }

                    _writeEncryptedSession(sessionCache);
                    return;
                }

                frontendLocalStorage[key] = val;
                save();
            }

            function removeItem(key) {
                if (key === LOCAL_STORAGE_KEYS.SESSION) {
                    sessionCache = null;
                    sessionLoaded = true;
                    window.localStorage.removeItem(SESSION_DISK_KEY);
                }

                if (key in frontendLocalStorage) {
                    delete frontendLocalStorage[key];
                    save();
                }
            }

            function clear() {
                frontendLocalStorage = {};
                sessionCache = null;
                sessionLoaded = true;
                window.localStorage.removeItem(SESSION_DISK_KEY);
                save();
            }

            function isExist(key) {
                if (key === LOCAL_STORAGE_KEYS.SESSION && _isSecureLocalStorageEnabled()) {
                    if (sessionLoaded) {
                        return !!sessionCache;
                    }

                    return !!window.localStorage.getItem(SESSION_DISK_KEY) || !!_readLegacyBlobSession();
                }

                return key in frontendLocalStorage;
            }

            function _normalizeSessionValue(value) {
                if (value === null || value === undefined || value === '') {
                    return null;
                }

                if (typeof value === 'string') {
                    try {
                        return JSON.parse(value);
                    } catch (err) {
                        return value;
                    }
                }

                return value;
            }

            function _writePlainSession(value) {
                if (value && Object.keys(value).length) {
                    window.localStorage.setItem(SESSION_DISK_KEY, JSON.stringify(value));
                } else {
                    window.localStorage.removeItem(SESSION_DISK_KEY);
                }
            }

            function _writeEncryptedSession(value) {
                if (!cryptoKey) {
                    _writePlainSession(value);
                    return;
                }

                if (!value || !Object.keys(value).length) {
                    window.localStorage.removeItem(SESSION_DISK_KEY);
                    return;
                }

                _encrypt(JSON.stringify(value), cryptoKey).then(function (envelope) {
                    window.localStorage.setItem(SESSION_DISK_KEY, envelope);
                }).catch(function (err) {
                    console.warn('[LocalStorage] Failed to encrypt session; storing plaintext', err);
                    _writePlainSession(value);
                });
            }

            function _loadSessionFromDiskPlain() {
                sessionLoaded = true;
                var raw = window.localStorage.getItem(SESSION_DISK_KEY);

                if (raw) {
                    try {
                        sessionCache = JSON.parse(raw);
                    } catch (err) {
                        sessionCache = null;
                    }
                    return $q.resolve();
                }

                var blobSession = _readLegacyBlobSession();
                if (blobSession) {
                    sessionCache = blobSession;
                    delete frontendLocalStorage[LOCAL_STORAGE_KEYS.SESSION];
                    save();
                    _writePlainSession(sessionCache);
                }

                return $q.resolve();
            }

            function _loadSessionFromDisk() {
                sessionLoaded = true;
                var raw = window.localStorage.getItem(SESSION_DISK_KEY);

                if (raw) {
                    if (!_isEncryptedEnvelope(raw)) {
                        try {
                            sessionCache = JSON.parse(raw);
                        } catch (err) {
                            sessionCache = null;
                        }

                        if (sessionCache) {
                            _writeEncryptedSession(sessionCache);
                        }

                        return $q.resolve();
                    }

                    return _decrypt(raw, cryptoKey).then(function (plaintext) {
                        sessionCache = JSON.parse(plaintext);
                    }).catch(function (err) {
                        console.warn('[LocalStorage] Failed to decrypt session', err);
                        sessionCache = null;
                    });
                }

                var blobSession = _readLegacyBlobSession();
                if (blobSession) {
                    sessionCache = blobSession;
                    delete frontendLocalStorage[LOCAL_STORAGE_KEYS.SESSION];
                    save();
                    _writeEncryptedSession(sessionCache);
                } else {
                    sessionCache = null;
                }

                return $q.resolve();
            }

            function _readLegacyBlobSession() {
                try {
                    var blob = JSON.parse(window.localStorage.getItem(BLOB_KEY) || '{}');
                    return blob[LOCAL_STORAGE_KEYS.SESSION] || null;
                } catch (err) {
                    return null;
                }
            }

            function _isEncryptedEnvelope(raw) {
                if (!raw || typeof raw !== 'string') {
                    return false;
                }

                try {
                    var parsed = JSON.parse(raw);

                    return !!(parsed &&
                        parsed.v === 1 &&
                        parsed.alg === 'AES-GCM' &&
                        typeof parsed.ct === 'string' &&
                        typeof parsed.iv === 'string');
                } catch (err) {
                    return false;
                }
            }

            function _encrypt(plaintext, key) {
                var iv = $window.crypto.getRandomValues(new Uint8Array(IV_LENGTH));

                return $q.resolve($window.crypto.subtle.encrypt(
                    { name: ALGORITHM, iv: iv },
                    key,
                    new TextEncoder().encode(plaintext)
                )).then(function (ciphertext) {
                    return JSON.stringify({
                        v: 1,
                        alg: 'AES-GCM',
                        iv: _arrayBufferToBase64(iv),
                        ct: _arrayBufferToBase64(ciphertext)
                    });
                });
            }

            function _decrypt(envelopeJson, key) {
                var envelope = typeof envelopeJson === 'string' ? JSON.parse(envelopeJson) : envelopeJson;
                var iv = _base64ToArrayBuffer(envelope.iv);
                var ciphertext = _base64ToArrayBuffer(envelope.ct);

                return $q.resolve($window.crypto.subtle.decrypt(
                    { name: ALGORITHM, iv: new Uint8Array(iv) },
                    key,
                    ciphertext
                )).then(function (plaintext) {
                    return new TextDecoder().decode(plaintext);
                });
            }

            function _getOrCreateKey() {
                return _tryNativeStorageGetOrCreate().catch(function () {
                    return _idbGetOrCreateKey();
                });
            }

            function _tryNativeStorageGetOrCreate() {
                var nativeStorage = _resolveNativeStorage();

                if (!nativeStorage) {
                    return $q.reject(new Error('Native storage is not available'));
                }

                return $q(function (resolve, reject) {
                    nativeStorage.getItem(
                        NATIVE_STORAGE_KEY,
                        function (value) {
                            _importRawKey(value).then(resolve, reject);
                        },
                        function () {
                            _generateAndStoreNativeKey(nativeStorage).then(resolve, reject);
                        }
                    );
                });
            }

            function _resolveNativeStorage() {
                if (!$window.cordova) {
                    return null;
                }

                if ($window.NativeStorage) {
                    return $window.NativeStorage;
                }

                if ($window.cordova.plugins && $window.cordova.plugins.NativeStorage) {
                    return $window.cordova.plugins.NativeStorage;
                }

                return null;
            }

            function _generateAndStoreNativeKey(nativeStorage) {
                return _generateRawKeyMaterial().then(function (rawKeyBytes) {
                    var keyBase64 = _bytesToBase64(rawKeyBytes);

                    return _importRawKey(keyBase64).then(function (key) {
                        return $q(function (resolve, reject) {
                            nativeStorage.setItem(
                                NATIVE_STORAGE_KEY,
                                keyBase64,
                                function () {
                                    resolve(key);
                                },
                                reject
                            );
                        });
                    });
                });
            }

            function _idbGetOrCreateKey() {
                return _idbOpen().then(function (db) {
                    return _idbGetKey(db).then(function (storedKey) {
                        if (storedKey) {
                            return _importRawKey(storedKey);
                        }

                        return _createAndStoreIdbKey(db);
                    });
                });
            }

            function _createAndStoreIdbKey(db) {
                return _generateRawKeyMaterial().then(function (rawKeyBytes) {
                    return _idbPutKey(db, rawKeyBytes).then(function () {
                        return _importRawKey(rawKeyBytes);
                    });
                });
            }

            function _idbOpen() {
                return $q(function (resolve, reject) {
                    var request = $window.indexedDB.open(DB_NAME, 1);

                    request.onupgradeneeded = function (event) {
                        var db = event.target.result;

                        if (!db.objectStoreNames.contains(STORE_NAME)) {
                            db.createObjectStore(STORE_NAME);
                        }
                    };

                    request.onsuccess = function (event) {
                        resolve(event.target.result);
                    };

                    request.onerror = function () {
                        reject(request.error);
                    };
                });
            }

            function _idbGetKey(db) {
                return $q(function (resolve, reject) {
                    var tx = db.transaction(STORE_NAME, 'readonly');
                    var store = tx.objectStore(STORE_NAME);
                    var request = store.get(KEY_ID);

                    request.onsuccess = function () {
                        resolve(request.result || null);
                    };

                    request.onerror = function () {
                        reject(request.error);
                    };
                });
            }

            function _idbPutKey(db, rawKeyBytes) {
                return $q(function (resolve, reject) {
                    var tx = db.transaction(STORE_NAME, 'readwrite');
                    var store = tx.objectStore(STORE_NAME);

                    store.put(_bytesToBase64(rawKeyBytes), KEY_ID);
                    tx.oncomplete = function () {
                        resolve();
                    };
                    tx.onerror = function () {
                        reject(tx.error);
                    };
                });
            }

            function _generateRawKeyMaterial() {
                if (!_isWebCryptoAvailable()) {
                    return $q.reject(new Error('Web Crypto API is not available'));
                }

                return $q.resolve($window.crypto.getRandomValues(new Uint8Array(KEY_BYTE_LENGTH)));
            }

            function _importRawKey(rawKey) {
                var keyBytes = _normalizeRawKeyMaterial(rawKey);

                if (!keyBytes || keyBytes.length !== KEY_BYTE_LENGTH) {
                    return $q.reject(new Error('Invalid stored key material'));
                }

                return $q.resolve($window.crypto.subtle.importKey(
                    'raw',
                    keyBytes,
                    { name: ALGORITHM, length: 256 },
                    false,
                    ['encrypt', 'decrypt']
                ));
            }

            function _normalizeRawKeyMaterial(storedKey) {
                if (!storedKey) {
                    return null;
                }

                if (typeof storedKey === 'string') {
                    return _base64ToBytes(storedKey);
                }

                if (storedKey instanceof ArrayBuffer) {
                    return new Uint8Array(storedKey);
                }

                if (ArrayBuffer.isView(storedKey)) {
                    return new Uint8Array(storedKey.buffer, storedKey.byteOffset, storedKey.byteLength);
                }

                return null;
            }

            function _arrayBufferToBase64(buffer) {
                var bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
                var binary = '';
                var i;

                for (i = 0; i < bytes.length; i++) {
                    binary += String.fromCharCode(bytes[i]);
                }

                return btoa(binary);
            }

            function _base64ToArrayBuffer(base64) {
                var binary = atob(base64);
                var bytes = new Uint8Array(binary.length);
                var i;

                for (i = 0; i < binary.length; i++) {
                    bytes[i] = binary.charCodeAt(i);
                }

                return bytes.buffer;
            }

            function _bytesToBase64(bytes) {
                return _arrayBufferToBase64(bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes));
            }

            function _base64ToBytes(base64) {
                return new Uint8Array(_base64ToArrayBuffer(base64));
            }
        }]);

})(angular, app, window);
